import {
  LEGACY_SERVICE_ALIAS,
  LEGACY_SERVICE_IDS,
} from "@acme/mdx/component-spec";
import { createFileRoute } from "@tanstack/react-router";
import { Fragment } from "react";
import { InnerCta } from "~/components/inside/inner-cta";
import { PageIntro, PageLabel } from "~/components/inside/page-intro";
import { PageScene } from "~/components/inside/page-scene";
import { PlainList } from "~/components/inside/plain-list";
import { QuietLink } from "~/components/inside/quiet-link";
import { FlowBoard } from "~/components/inside/services/flow-board";
import { SheetArtwork } from "~/components/inside/sheets";
import { usePageEngagement } from "~/hooks/use-page-engagement";
import { publicCacheHeaders } from "~/lib/cache-headers";
import { getCapabilities, getDeliveryList } from "~/lib/constants/capabilities";
import { getServicePages } from "~/lib/constants/service-pages";
import { seo } from "~/lib/seo";
import {
  generateStructuredDataGraph,
  getServicesPageSchemas,
} from "~/lib/structured-data";
import * as m from "~/paraglide/messages.js";

export const Route = createFileRoute("/(public)/services/")({
  component: ServicesPage,
  head: () => {
    const seoData = seo({
      description: m.meta_services_description(),
      keywords:
        "AI automation services, custom AI agents, AI agent development, custom AI chatbot development, RAG development services, AI knowledge base development, AI workflow automation, n8n automation, AI integration services, LLM integration, AI document processing, Lindevo",
      path: "/services",
      title: m.meta_services_title(),
    });
    const structuredData = generateStructuredDataGraph(
      getServicesPageSchemas()
    );
    return {
      links: seoData.links,
      meta: seoData.meta,
      scripts: [
        {
          children: structuredData,
          type: "application/ld+json",
        },
      ],
    };
  },
  headers: publicCacheHeaders,
});

/**
 * The five kinds of system Lindevo builds, each shown as the artifact it produces.
 *
 * The bands alternate sides so the page reads as five different shapes of
 * system rather than a column of identical cards, and each one hands off to
 * the matching example on the pain-points page and to the dedicated page for
 * each service inside it.
 */
function ServicesPage() {
  usePageEngagement({ pagePath: "/services" });

  const capabilities = getCapabilities();
  const servicePages = getServicePages();

  return (
    <>
      <PageScene variant="split">
        <div>
          <PageLabel>{m.services_label()}</PageLabel>
          <h1>
            {m.services_title_1()} <br />
            {m.services_title_2()} <br />
            {m.services_title_3()}
          </h1>
          <PageIntro>{m.services_intro()}</PageIntro>
          <div className="actions">
            <a className="button button-solid" href="#capabilities">
              {m.services_hero_cta()} <span aria-hidden="true">↓</span>
            </a>
          </div>
        </div>

        <FlowBoard />
      </PageScene>

      <div className="wrap">
        <div className="jump-strip" id="capabilities">
          <p>{m.services_jump_title()}</p>
          <nav aria-label={m.services_jump_label()}>
            {capabilities.map((capability) => (
              <a href={`#${capability.id}`} key={capability.id}>
                {capability.label}
              </a>
            ))}
          </nav>
        </div>

        {capabilities.map((capability, index) => (
          <Fragment key={capability.id}>
            {/* Links from before the five lines (`#knowledge`, `#workflows`, `#tools`) land on their line. */}
            {LEGACY_SERVICE_IDS.filter(
              (legacy) => LEGACY_SERVICE_ALIAS[legacy] === capability.id
            ).map((legacy) => (
              <div id={legacy} key={legacy} />
            ))}
            <section
              className={
                index % 2 === 1 ? "capability is-reversed" : "capability"
              }
              id={capability.id}
            >
              <div className="capability-copy">
                <PageLabel>{capability.label}</PageLabel>
                <h2>
                  {capability.title[0]} <br />
                  {capability.title[1]}
                </h2>
                <p>{capability.body}</p>
                <PlainList items={capability.points} />
                <div className="capability-links">
                  {servicePages
                    .filter((page) => page.capabilityId === capability.id)
                    .map((page) => (
                      <QuietLink key={page.slug} to={`/services/${page.slug}`}>
                        {page.nav}
                      </QuietLink>
                    ))}
                  <QuietLink
                    hash={capability.link.hash}
                    to={capability.link.to}
                  >
                    {capability.link.label}
                  </QuietLink>
                </div>
              </div>

              <div className="visual-field">
                <SheetArtwork art={capability.art} />
                <p className="small-note">{capability.artNote}</p>
              </div>
            </section>
          </Fragment>
        ))}

        <section className="service-bottom">
          <div>
            <h2>{m.services_delivery_title()}</h2>
            <p>{m.services_delivery_body()}</p>
            <QuietLink to="/how-we-work">{m.services_delivery_cta()}</QuietLink>
          </div>
          <dl className="delivery-list">
            {getDeliveryList().map((item) => (
              <div key={item.term}>
                <dt>{item.term}</dt>
                <dd>{item.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>

      <InnerCta
        lede={m.services_cta_lede()}
        secondary={{ label: m.services_cta_secondary(), to: "/pain-points" }}
        title={
          <>
            {m.services_cta_title_1()} <br />
            {m.services_cta_title_2()}
          </>
        }
      />
    </>
  );
}
